/**
 * TournamentClock.js
 * 
 * Enhanced tournament clock display with customizable settings
 * - Respects display toggle settings
 * - Shows/hides elements based on configuration
 * - Displays scrolling prize structure
 * - Applies custom colors and messages
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSocket } from '../../contexts/SocketContext';
import { apiService } from '../../services/api';

// Calculate time until next break
// Calculate time until next break
const calculateTimeToNextBreak = (structure, currentLevel, timeRemainingSeconds) => {
  if (!structure || !currentLevel) return null;
  
  // Find next break in structure
  let nextBreakLevel = null;
  for (let i = 0; i < structure.length; i++) {
    if (structure[i].level > currentLevel && structure[i].type === 'break') {
      nextBreakLevel = structure[i];
      break;
    }
  }
  
  if (!nextBreakLevel) {
    return { noMoreBreaks: true }; // CHANGED: Return this instead of null
  }
  
  // Calculate total time: remaining time in current level + time for all levels until break
  let totalSeconds = timeRemainingSeconds;
  
  for (let i = 0; i < structure.length; i++) {
    const level = structure[i];
    if (level.level > currentLevel && level.level < nextBreakLevel.level) {
      totalSeconds += (level.duration * 60); // Convert minutes to seconds
    }
  }
  
  return {
    totalSeconds,
    breakDuration: nextBreakLevel.duration,
    noMoreBreaks: false
  };
};

// Format seconds to HH:MM:SS
const formatTimeHHMMSS = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const TournamentClock = ({ tournamentId }) => {
  console.log('TournamentClock received tournamentId:', tournamentId);
  
  const { connected, tournamentData, joinTournament } = useSocket();
  const [clockData, setClockData] = useState(null);
  const [clockSettings, setClockSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localTimeRemaining, setLocalTimeRemaining] = useState(null);

  // Helper function to safely convert values to numbers
  const safeNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };

  // Helper functions
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    // If less than an hour, show only MM:SS
    if (hours === 0) {
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    
    // If an hour or more, show HH:MM:SS
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatBlinds = (blinds) => {
    if (!blinds) return 'N/A';
    if (blinds.type === 'break') return 'BREAK';
    
    const ante = blinds.ante > 0 ? ` (${blinds.ante})` : '';
    return `${blinds.small_blind}/${blinds.big_blind}${ante}`;
  };

  // Load clock settings
  const loadClockSettings = useCallback(async () => {
    try {
      const hostname = window.location.hostname;
      let baseUrl = 'http://localhost:3000';
      if (hostname === '10.0.0.28') {
        baseUrl = `https://${hostname}/api`;
      } else if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        baseUrl = `https://${hostname}/api`;
      }
      
      const response = await fetch(`${baseUrl}/tournaments/${tournamentId}/clock-settings`);
      const settings = await response.json();
      setClockSettings(settings);
    } catch (error) {
      console.error('Error loading clock settings:', error);
      // Use default settings if loading fails
      setClockSettings({
        show_tournament_name: true,
        show_timer: true,
        show_level_info: true,
        show_blinds: true,
        show_next_blinds: true,
        show_player_count: true,
        show_average_stack: true,
        show_prize_pool: true,
        show_total_entries: true,
        show_chips_in_play: false,
        show_rebuys_addons: true,
        show_dealer_code: true,
        show_prize_structure: false,
        show_custom_message: false,
        background_color: '#1a1a1a',
        text_color: '#ffffff',
        accent_color: '#4CAF50'
      });
    }
  }, [tournamentId]);

  // API functions
  const loadClockData = useCallback(async () => {
    try {
      console.log('=== LOADING CLOCK DATA ===');
      console.log('Tournament ID:', tournamentId);
      
      const response = await apiService.getTournamentClock(tournamentId);
      console.log('API Response:', response);
      console.log('Clock data received:', response.data);
      
      setClockData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading clock data:', error);
      setLoading(false);
    }
  }, [tournamentId]);

  const reloadClockData = useCallback(async () => {
    try {
      console.log('=== RELOADING CLOCK DATA ===');
      const response = await apiService.getTournamentClock(tournamentId);
      console.log('Reloaded clock data:', response.data);
      setClockData(response.data);
    } catch (error) {
      console.error('Error reloading clock data:', error);
    }
  }, [tournamentId]);

  // Effects
  useEffect(() => {
    if (tournamentId && connected) {
      console.log('Joining tournament and loading data...');
      joinTournament(tournamentId);
      loadClockData();
      loadClockSettings();
    }
  }, [tournamentId, connected, joinTournament, loadClockData, loadClockSettings]);

  // Update local data when WebSocket data changes
  useEffect(() => {
    if (tournamentData) {
      console.log('=== WEBSOCKET DATA RECEIVED ===');
      
      // TEMPORARY: Only update if WebSocket data has the required fields
      if (tournamentData.total_entries !== undefined && tournamentData.tournament_start_time !== undefined) {
        console.log('WebSocket data is complete, updating clock data');
        setClockData(tournamentData);
      } else {
        console.log('WebSocket data is incomplete, keeping API data');
      }
      
      // If the structure or level changed significantly, reload full data
      if (tournamentData.current_level !== clockData?.current_level) {
        console.log('Level changed, reloading full data...');
        reloadClockData();
      }
    }
  }, [tournamentData, clockData?.current_level, reloadClockData]);

  // Set local time when clock data changes
  useEffect(() => {
    if (clockData) {
      console.log('Setting local time from clock data:', clockData.time_remaining_seconds);
      setLocalTimeRemaining(clockData.time_remaining_seconds);
    }
  }, [clockData]);

  // Main countdown logic
  useEffect(() => {
    if (!clockData) return;

    console.log('=== COUNTDOWN EFFECT ===');
    console.log('Status:', clockData.status);

    // Check if tournament hasn't started yet and calculate countdown to start
    const tournamentNotStarted = clockData.status === 'registration' && !clockData.started_at;
    
    if (tournamentNotStarted && clockData.tournament_start_time) {
      console.log('Tournament not started, setting up countdown...');
      
      const now = new Date();
      const startTime = new Date(clockData.tournament_start_time);
      const timeUntilStartMs = startTime - now;
      const timeUntilStartSeconds = Math.max(0, Math.floor(timeUntilStartMs / 1000));
      
      if (timeUntilStartSeconds > 0) {
        console.log('Starting countdown to tournament start...');
        setLocalTimeRemaining(timeUntilStartSeconds);
        
        const timer = setInterval(() => {
          const currentNow = new Date();
          const newTimeUntilStart = Math.max(0, Math.floor((startTime - currentNow) / 1000));
          
          if (newTimeUntilStart <= 0) {
            console.log('Tournament should start now, reloading...');
            clearInterval(timer);
            loadClockData();
            return;
          }
          
          setLocalTimeRemaining(newTimeUntilStart);
        }, 1000);

        return () => clearInterval(timer);
      } else {
        console.log('Tournament should have started, showing ready state');
        setLocalTimeRemaining(0);
        return;
      }
    }

    // Regular tournament timer (running or break)
    if (clockData.status === 'running' || clockData.status === 'break') {
      console.log('Starting tournament timer for status:', clockData.status);
      let currentTime = clockData.time_remaining_seconds;
      setLocalTimeRemaining(currentTime);

      const timer = setInterval(() => {
        currentTime -= 1;
        if (currentTime <= 0) {
          setLocalTimeRemaining(0);
          clearInterval(timer);
        } else {
          setLocalTimeRemaining(currentTime);
        }
      }, 1000);

      return () => clearInterval(timer);
    }

    // For other statuses (paused, finished, etc.), just set the current time
    console.log('Setting time for status:', clockData.status);
    setLocalTimeRemaining(clockData.time_remaining_seconds);

  }, [clockData, loadClockData]);

// Force full-screen mode - hide nav and make full width
useEffect(() => {
  // Hide any navigation/header elements
  const hideElements = () => {
    const nav = document.querySelector('nav');
    const header = document.querySelector('header');
    const appHeader = document.querySelector('.App-header');
    const appNav = document.querySelector('.App-nav');
    const appMain = document.querySelector('.App-main');
    
    if (nav) nav.style.display = 'none';
    if (header) header.style.display = 'none';
    if (appHeader) appHeader.style.display = 'none';
    if (appNav) appNav.style.display = 'none';
    
    // Force main container to full width
    if (appMain) {
      appMain.style.margin = '0';
      appMain.style.padding = '0';
      appMain.style.maxWidth = '100%';
      appMain.style.width = '100%';
    }
  };
  
  // Force body and root to full width - NO OVERFLOW CHANGES
  document.body.style.margin = '0';
  document.body.style.padding = '0';
  
  const root = document.getElementById('root');
  if (root) {
    root.style.margin = '0';
    root.style.padding = '0';
    root.style.width = '100%';
  }
  
  hideElements();
  
  // Cleanup on unmount
  return () => {
    const nav = document.querySelector('nav');
    const header = document.querySelector('header');
    const appHeader = document.querySelector('.App-header');
    const appNav = document.querySelector('.App-nav');
    const appMain = document.querySelector('.App-main');
    
    if (nav) nav.style.display = '';
    if (header) header.style.display = '';
    if (appHeader) appHeader.style.display = '';
    if (appNav) appNav.style.display = '';
    if (appMain) {
      appMain.style.margin = '';
      appMain.style.padding = '';
      appMain.style.maxWidth = '';
      appMain.style.width = '';
    }
    
    document.body.style.margin = '';
    document.body.style.padding = '';
    
    const root = document.getElementById('root');
    if (root) {
      root.style.margin = '';
      root.style.padding = '';
      root.style.width = '';
    }
  };
}, []);

  // ===== LEVEL CHANGE ALERT =====
  const prevLevelRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(false);

  const enableSound = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    audioCtxRef.current.resume();
    setSoundEnabled(true);
  };

  const playLevelAlert = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    // Three short chimes
    [0, 0.35, 0.7].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.6, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.32);
    });
  }, []);

  useEffect(() => {
    const level = clockData?.current_level;
    if (level == null) return;
    // Skip the first load so it doesn't chime when the page opens
    if (prevLevelRef.current !== null && level !== prevLevelRef.current) {
      playLevelAlert();
    }
    prevLevelRef.current = level;
  }, [clockData?.current_level, playLevelAlert]);
  // ===== END LEVEL CHANGE ALERT =====

  // Render logic
  if (loading || !clockSettings) {
    return <div className="container">Loading tournament clock...</div>;
  }

  if (!clockData) {
    return <div className="container">No tournament data available</div>;
  }

  // Calculate display variables
  const tournamentNotStarted = clockData.status === 'registration' && !clockData.started_at;
  let timeUntilStart = 0;
  let showCountdownToStart = false;
  
  if (tournamentNotStarted && clockData.tournament_start_time) {
    const now = new Date();
    const startTime = new Date(clockData.tournament_start_time);
    timeUntilStart = Math.max(0, Math.floor((startTime - now) / 1000));
    showCountdownToStart = timeUntilStart > 0;
  }

  // Calculate real-time statistics with proper safety
  const totalEntries = safeNumber(clockData.total_entries);
  const playersRemaining = safeNumber(clockData.players_remaining);
  const totalPrizePool = safeNumber(clockData.total_prize_pool);
  const averageStack = safeNumber(clockData.average_stack || clockData.starting_chips);
  const totalChipsInPlay = safeNumber(clockData.total_chips_in_play);

  return (
  <div style={{ 
    background: clockSettings.background_color || '#1a1a1a', 
    color: clockSettings.text_color || 'white', 
    minHeight: '100vh',
    width: '100vw',
    margin: '0',
    padding: '0',
    fontFamily: 'Arial, sans-serif',
    position: 'relative',
    overflow: 'hidden'
  }}>

        {!soundEnabled && (
        <button
          onClick={enableSound}
          style={{
            position: 'fixed', top: '15px', right: '15px', zIndex: 1000,
            padding: '8px 14px', fontSize: '14px', borderRadius: '6px',
            border: 'none', background: '#4CAF50', color: '#fff', cursor: 'pointer'
          }}
        >
          🔔 Enable sound
        </button>
      )}

{/* Grayscale logo watermark — rendered via portal to document.body so no
          transformed ancestor can trap its fixed positioning (prevents scroll drift) */}
      {createPortal(
        <img
          src="/receipt-logo.png"
          alt=""
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '55%',
            maxHeight: '70%',
            filter: 'grayscale(100%) brightness(1.8)',
            opacity: 0.15,
            pointerEvents: 'none',
            zIndex: 0
          }}
        />,
        document.body
      )}

      {/* Prize Structure Display */}
      {clockSettings.show_prize_structure && clockSettings.prize_structure_data && (
        <PrizeStructureDisplay 
          prizes={clockSettings.prize_structure_data}
          position={clockSettings.prize_structure_position}
          scroll={clockSettings.prize_structure_scroll}
          accentColor={clockSettings.accent_color}
        />
      )}

      {/* Tournament Name */}
      {clockSettings.show_tournament_name && (
        <div style={{ 
          textAlign: 'center', 
          fontSize: '36px', 
          fontWeight: 'bold',
          marginBottom: '20px',
          borderBottom: `2px solid ${clockSettings.accent_color || '#333'}`,
          paddingBottom: '20px',
          width: '100%'
        }}>
          {clockData.tournament_name}
        </div>
      )}

      {/* Custom Message */}
      {clockSettings.show_custom_message && clockSettings.custom_message && (
        <div style={{
          textAlign: 'center',
          fontSize: '24px',
          color: clockSettings.accent_color || '#FFD700',
          marginBottom: '20px',
          padding: '15px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '8px'
        }}>
          {clockSettings.custom_message}
        </div>
      )}

      {/* Main Clock Display */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        
{/* Left Column - Stats */}
        <div style={{ flex: '1', textAlign: 'center' }}>
          {clockSettings.show_average_stack && (
            <div style={{ marginBottom: '40px' }}>
              <div style={{ fontSize: '24px', color: '#ccc' }}>Average Stack</div>
              <div style={{ fontSize: '48px', fontWeight: 'bold', color: clockSettings.accent_color || '#4CAF50' }}>
                {tournamentNotStarted ? 
                  safeNumber(clockData.starting_chips).toLocaleString() : 
                  averageStack.toLocaleString()
                }
              </div>
              {!tournamentNotStarted && clockSettings.show_chips_in_play && totalChipsInPlay > 0 && (
                <div style={{ fontSize: '16px', color: '#999', marginTop: '5px' }}>
                  {totalChipsInPlay.toLocaleString()} total chips
                </div>
              )}
            </div>
          )}
          
          {clockSettings.show_player_count && (
            <div>
              <div style={{ fontSize: '24px', color: '#ccc' }}>Players</div>
              <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#FF9800' }}>
                {tournamentNotStarted ? 
                  `${totalEntries} Registered` : 
                  `${playersRemaining}/${totalEntries}`
                }
              </div>
              {!tournamentNotStarted && clockSettings.show_rebuys_addons && 
               (clockData.total_rebuys > 0 || clockData.total_addons > 0) && (
                <div style={{ fontSize: '16px', color: '#999', marginTop: '5px' }}>
                  {clockData.total_rebuys > 0 && `${clockData.total_rebuys} rebuy${clockData.total_rebuys > 1 ? 's' : ''}`}
                  {clockData.total_rebuys > 0 && clockData.total_addons > 0 && ' • '}
                  {clockData.total_addons > 0 && `${clockData.total_addons} addon${clockData.total_addons > 1 ? 's' : ''}`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center - Timer and Level */}
        <div style={{ flex: '1', textAlign: 'center' }}>
          {clockSettings.show_level_info && (
            <div style={{ fontSize: '24px', color: '#ccc', marginBottom: '10px' }}>
              {showCountdownToStart ? 'Tournament Starts In' :
               tournamentNotStarted ? 'Tournament Ready to Start' :
               clockData.status === 'break' ? 'BREAK' : `Level ${clockData.current_level}`}
            </div>
          )}
          {clockSettings.show_timer && (
            <div style={{ 
              fontSize: '120px', 
              fontWeight: 'bold', 
              color: showCountdownToStart ? '#FFD700' :
                     tournamentNotStarted ? clockSettings.accent_color || '#4CAF50' :
                     clockData.status === 'break' ? '#FF9800' : 
                     localTimeRemaining === 0 ? '#ff4444' : clockSettings.text_color || '#fff',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}>
              {showCountdownToStart ? 
                formatTime(timeUntilStart) :
                tournamentNotStarted ? 'READY' :
                formatTime(localTimeRemaining !== null ? localTimeRemaining : clockData.time_remaining_seconds)
              }
            </div>
          )}
          {localTimeRemaining === 0 && clockData.status === 'running' && (
            <div style={{ 
              fontSize: '24px', 
              color: '#ff4444', 
              fontWeight: 'bold',
              animation: 'blink 1s infinite'
            }}>
              LEVEL ADVANCING...
            </div>
          )}
          <div style={{ 
            fontSize: '18px', 
            color: showCountdownToStart ? '#FFD700' :
                   tournamentNotStarted ? clockSettings.accent_color || '#4CAF50' :
                   clockData.status === 'running' ? clockSettings.accent_color || '#4CAF50' : 
                   clockData.status === 'break' ? '#FF9800' : '#FF5722',
            marginTop: '10px'
          }}>
            {showCountdownToStart ? 'SCHEDULED' :
             tournamentNotStarted ? 'READY TO START' : 
             clockData.status.toUpperCase()}
          </div>
        </div>

        {/* Right Column - Blinds */}
        <div style={{ flex: '1', textAlign: 'center' }}>
          {clockSettings.show_blinds && (
            <div style={{ marginBottom: '40px' }}>
              <div style={{ fontSize: '24px', color: '#ccc' }}>
                {tournamentNotStarted ? 'Starting Blinds' : 'Current Level'}
              </div>
              <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#2196F3' }}>
                {formatBlinds(clockData.current_blinds)}
              </div>
            </div>
          )}
          
          {clockSettings.show_next_blinds && (
            <div>
              <div style={{ fontSize: '24px', color: '#ccc' }}>Next Level</div>
              <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#9E9E9E', opacity: '0.4' }}>
                {formatBlinds(clockData.next_blinds)}
              </div>
            </div>
          )}
        </div>
      </div>

{/* Next Break Countdown */}
{(() => {
  const breakInfo = calculateTimeToNextBreak(
    clockData.structure,
    clockData.current_level,
    clockData.time_remaining_seconds
  );
  
  if (!breakInfo) return null;
  
  // Show "No More Breaks" message
  if (breakInfo.noMoreBreaks) {
    return null;
  }
  
  // Show countdown to next break
  return (
    <div style={{
      textAlign: 'center',
      padding: '15px 30px',
      background: '#17a2b8',
      borderRadius: '8px',
      maxWidth: '500px',
      margin: '20px auto 10px auto',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#fff'
      }}>
        Next Break in {formatTimeHHMMSS(breakInfo.totalSeconds)} for {breakInfo.breakDuration} minutes
      </div>
    </div>
  );
})()}
      
{/* LPS Pool Display - EXISTING CODE BELOW */}

      {/* LPS Pool Display */}
      {clockData.lps_enabled && clockData.lps_total_entries > 0 && (
        <div style={{
          textAlign: 'center',
          padding: '15px 30px',
          background: '#ffc107',
          borderRadius: '8px',
          maxWidth: '400px',
          margin: '30px auto',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            marginBottom: '8px'
          }}>
            LAST PERSON STANDING
          </div>
          <div style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: '#000'
          }}>
            ${clockData.total_lps_pool.toFixed(2)}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#000',
            opacity: 0.8,
            marginTop: '4px'
          }}>
            {clockData.lps_active_players}/{clockData.lps_total_entries} {clockData.lps_total_entries === 1 ? 'Entry' : 'Entries'}  
          </div>
        </div>
      )}

{/* Payout Structure Display - Scrolling Ticker */}
{clockData.show_payouts && clockData.payout_structure && clockData.payout_structure.length > 0 && (
  <div style={{
    overflow: 'hidden',
    background: '#28a745',
    padding: '8px 0',
    margin: '10px 0',
    position: 'relative'
  }}>
    <div style={{
      display: 'inline-block',
      color: '#fff',
      fontSize: '18px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      animation: 'scrollPrizes 40s linear infinite'
    }}>
      {clockData.payout_structure.map((payout, index) => (
        <span key={index} style={{ marginRight: '40px' }}>
          {payout.position}: ${payout.amount.toFixed(2)} - {payout.label ? ` ${payout.label}` : ''}
        </span>
      ))}
    </div>
    
    <style>{`
      @keyframes scrollPrizes {
        0% { transform: translateX(100%); }
        100% { transform: translateX(-100%); }
      }
    `}</style>
  </div>
)}
      
      {/* Bottom Info Bar */}
      <div style={{ 
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        background: '#333',
        padding: '15px',
        textAlign: 'center',
        fontSize: '18px'
      }}>
        {clockSettings.show_prize_pool && (
          <span>Prize Pool: ${totalPrizePool.toFixed(2)} | </span>
        )}
        <span>Status: {connected ? ' 🟢 Connected' : ' 🔴 Disconnected'}</span>
      </div>

      {/* CSS Animation for blinking */}
      <style>{`
        @keyframes blink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

/**
 * Prize Structure Display Component
 */
const PrizeStructureDisplay = ({ prizes, position, scroll, accentColor }) => {
  if (!prizes || prizes.length === 0) return null;

  const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const positionStyles = {
    top: { top: 0, left: 0, right: 0, flexDirection: 'row' },
    bottom: { bottom: '60px', left: 0, right: 0, flexDirection: 'row' },
    left: { top: 0, left: 0, bottom: '60px', flexDirection: 'column', width: '200px' },
    right: { top: 0, right: 0, bottom: '60px', flexDirection: 'column', width: '200px' }
  };

  const containerStyle = {
    position: 'fixed',
    background: 'rgba(0, 0, 0, 0.8)',
    padding: '15px',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    ...positionStyles[position]
  };

  const prizeContent = (
    <div style={{ 
      display: 'flex', 
      gap: '30px',
      flexDirection: position === 'left' || position === 'right' ? 'column' : 'row',
      alignItems: 'center'
    }}>
      <div style={{ 
        fontSize: '20px', 
        fontWeight: 'bold',
        color: accentColor || '#FFD700',
        whiteSpace: 'nowrap'
      }}>
        💰 PRIZE STRUCTURE
      </div>
      {prizes.map((prize, index) => (
        <div key={index} style={{ 
          display: 'flex', 
          gap: '10px',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}>
          <span style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#fff'
          }}>
            {getOrdinal(prize.position)}:
          </span>
          <span style={{ fontSize: '18px', color: '#4CAF50', fontWeight: 'bold' }}>
            ${prize.prize.toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );

  if (scroll && (position === 'top' || position === 'bottom')) {
    return (
      <div style={containerStyle}>
        <div style={{ 
          overflow: 'hidden',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            gap: '30px',
            animation: 'scroll-left 30s linear infinite'
          }}>
            {prizeContent}
            {prizeContent}
          </div>
        </div>
        <style>{`
          @keyframes scroll-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </div>
    );
  }

  return <div style={containerStyle}>{prizeContent}</div>;
};

export default TournamentClock;